import { ofType } from 'redux-observable';
import { forkJoin, merge } from 'rxjs';
import {
    filter,
    mergeMap,
    switchMap,
    take,
    withLatestFrom,
} from 'rxjs/operators';

import { appReady, routeToLocation } from '@perpay-web/fintech/actions/router';
import { virtualProvisioningModalSetIsModalOpen } from '@perpay-web/fintech/components/composite/VirtualProvisioningModal/useVirtualProvisioningModalContext';
import {
    APPLE_PAY_DIGITAL_WALLET_PROVISIONING_ENDPOINT,
    ANDROID_PAY_DIGITAL_WALLET_PROVISIONING_ENDPOINT,
} from '@perpay-web/fintech/constants/urls';
import {
    ADD_TO_WALLET_STEP,
    SUCCESS_STEP,
    virtualProvisioningStepsSetStep,
} from '@perpay-web/fintech/hooks/useVirtualProvisioningModalStepsContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { getJWTData } from '@perpay-web/fintech/selectors/authentication';
import {
    addToWalletSuccess,
    addToWalletError,
    startWalletProvisioning as startWalletProvisioningAction,
    startWalletProvisioningSuccess,
    startWalletProvisioningError,
    virtualProvisioningProvisionRequest as virtualProvisioningProvisionRequestAction,
    virtualProvisioningProvisionSuccess,
    virtualProvisioningProvisionError,
    continueWalletProvisioning,
    continueWalletProvisioningSuccess,
    continueWalletProvisioningError,
    nativeWalletStateInitialized,
} from '@perpay-web/native-client/actions/nativeWallet';
import { getHasPaymentPass } from '@perpay-web/native-client/selectors/nativeWallet';
import { dispatchObservable } from '@perpay-web/observable/dispatchObservable';
import { handleErrorMessageWithFallback } from '@perpay-web/observable/operators/handleErrorMessageWithFallback';
import { getRouterState } from '@perpay-web/services/router';
import { emitOrTimeout } from '@perpay-web/utils/observable';
import { getURLSearchParam } from '@perpay-web/utils/urlUtils';

export const addToWallet = (action$, state$) =>
    action$.pipe(
        ofType(addToWalletSuccess().type),
        switchMap((action) =>
            dispatchObservable({
                action$,
                state$,
                initialDispatch: (state) => {
                    const { cardToken, lastFour } = action.payload;
                    const { fullName: cardholderName } = getJWTData(state);
                    const cardProductName = 'Perpay Card';
                    const panReferenceID = 'TOKEN_PLACEHOLDER';

                    return [
                        startWalletProvisioningAction({
                            lastFour,
                            cardholderName,
                            cardProductName,
                            panReferenceID,
                            cardToken,
                        }),
                    ];
                },
                waitFor: [startWalletProvisioningSuccess().type],
                waitForDispatch: (state, [result]) => [
                    virtualProvisioningProvisionRequestAction(result.payload),
                ],
                errors: [startWalletProvisioningError().type],
                errorDispatch: (error) => [addToWalletError(error)],
                timeout: 0,
            }),
        ),
        handleErrorMessageWithFallback((error) => [addToWalletError(error)]),
    );

export const virtualProvisioningProvisionRequest = (
    action$,
    state$,
    { post },
) =>
    action$.pipe(
        ofType(virtualProvisioningProvisionRequestAction().type),
        switchMap((action) => {
            const { agent, payload } = action.payload;

            const endpoint =
                agent === 'ios'
                    ? APPLE_PAY_DIGITAL_WALLET_PROVISIONING_ENDPOINT
                    : ANDROID_PAY_DIGITAL_WALLET_PROVISIONING_ENDPOINT;

            return emitOrTimeout(post(endpoint, payload));
        }),
        switchMap((results) =>
            dispatchObservable({
                action$,
                state$,
                initialDispatch: [continueWalletProvisioning(results)],
                waitFor: [continueWalletProvisioningSuccess().type],
                waitForDispatch: (state, action) => [
                    virtualProvisioningProvisionSuccess(action.payload),
                    virtualProvisioningStepsSetStep(SUCCESS_STEP),
                    virtualProvisioningModalSetIsModalOpen(true),
                ],
                errors: [continueWalletProvisioningError().type],
                errorDispatch: (error) => [
                    virtualProvisioningProvisionError(error),
                ],
                timeout: 0,
            }),
        ),
        handleErrorMessageWithFallback((error) => [
            virtualProvisioningProvisionError(error),
        ]),
    );

export const openAddToWalletLink = (action$, state$, { nativeState$ }) =>
    merge(
        forkJoin(
            action$.pipe(ofType(appReady().type), take(1)),
            action$.pipe(ofType(nativeWalletStateInitialized().type), take(1)),
        ),
        action$.pipe(
            ofType(routeToLocation().type),
            filter((action) => {
                const { path } = action.payload;

                return path && path.startsWith(paths.cardDetails.path);
            }),
        ),
    ).pipe(
        withLatestFrom(nativeState$),
        mergeMap(([unusedTrigger, nativeState]) => {
            const { search } = getRouterState().location;
            if (getURLSearchParam('add-to-wallet', search) !== 'open') {
                return [];
            }

            if (getHasPaymentPass(nativeState)) {
                // We already have a card added, so just show the success page
                return [
                    virtualProvisioningStepsSetStep(SUCCESS_STEP),
                    virtualProvisioningModalSetIsModalOpen(true),
                ];
            }

            return [
                virtualProvisioningStepsSetStep(ADD_TO_WALLET_STEP),
                virtualProvisioningModalSetIsModalOpen(true),
            ];
        }),
    );
